import React, { FC, useEffect, useRef, useState } from 'react';
import Dropdown from '~/src/components/form-components/dropdown/dropdown';
import { useDebounceHook } from '~/src/hooks/use-debounce.hook';
import { useFetchAddressOrganizations } from '~/src/repositories/organizations/hooks/use-fetch-address-organization.hook';
import {
    getAddressValidationError,
    getAddressComponentItem,
    prepareDataFromDropdownAddress,
} from '~/src/components/common-components/address-field/address-field.utils';
import { getEmptyOutputData } from '~/src/helpers/empty-output';
import { objectIsEmpty } from '~/src/helpers/object-is-empty.helper';
import {
    BASE_ADDRESS_ERROR_TEXT,
    EMPTY_ADDRESS_TEXT,
    ERROR_FIELD_TEXT,
    INCOMPLETE_ADDRESS_TEXT,
} from '~/src/components/common-components/address-field/address-field.constants';
import type { AddressSuggestionProps } from '../dropdown-address-item/dropdown-address-item.types';
import type { DropDownFieldProps } from '~/src/models/types/fields.types';
import type { CustomInputChangeInterface } from '~/src/components/form-components/dropdown/dropdown.types';
import type {
    SuggestedAddressType,
    SuggestedAddressTypeKeys,
} from '~/src/components/common-components/address-field/address-field.types';

export const AddressField: FC<DropDownFieldProps & { onValidationChange?: (hasError: boolean) => void }> = ({
    required,
    validated,
    onSuggestedValueChange,
    onValueChange,
    onValidationChange,
    ...props
}) => {
    const [addressValue, setAddressValue] = useState('');
    const [addressError, setAddressError] = useState('');
    const [addressDataState, setAddressDataState] = useState<AddressSuggestionProps[]>([]);
    const [suggestedAddressData, setSuggestedAddressData] = useState<SuggestedAddressType>({});
    const [isFullAddress, setIsFullAddress] = useState(false);
    const [isSelectedFromSuggest, setIsSelectedFromSuggest] = useState(false);
    const validatedChangeRef = useRef(false);
    const validatedSuggestRef = useRef(false);
    const initial = useRef(true);
    const isFromSuggest = useRef(false);
    const suggestTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const blurCheckTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const debouncedAddressValue = useDebounceHook(addressValue);
    const { refetch } = useFetchAddressOrganizations(debouncedAddressValue, setAddressDataState);

    useEffect(() => {
        debouncedAddressValue && refetch().then();
    }, [debouncedAddressValue]);

    useEffect(() => {
        !required && setAddressError('');
    }, [required]);

    useEffect(() => {
        if (objectIsEmpty(suggestedAddressData)) {
            return;
        }

        if (!isFullAddress) {
            setAllValidatesCombinedError(INCOMPLETE_ADDRESS_TEXT);
            return;
        }

        const requiredFields: SuggestedAddressTypeKeys[] = ['house'];

        const addressCombinedError = getAddressValidationError(
            suggestedAddressData,
            requiredFields,
            BASE_ADDRESS_ERROR_TEXT,
        );

        setAllValidatesCombinedError(addressCombinedError);
    }, [suggestedAddressData, isFullAddress]);

    useEffect(() => {
        return () => {
            if (suggestTimerRef.current) {
                clearTimeout(suggestTimerRef.current);
            }
            if (blurCheckTimerRef.current) {
                clearTimeout(blurCheckTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        let hasError: boolean;

        if (!addressValue) {
            hasError = Boolean(required);
        } else if (!isSelectedFromSuggest) {
            hasError = true;
        } else {
            hasError = Boolean(addressError) || !isFullAddress;
        }

        if (!addressValue && !required) {
            hasError = false;
        }

        const timer = setTimeout(() => {
            onValidationChange?.(hasError);
        }, 50);

        return () => clearTimeout(timer);
    }, [addressError, isFullAddress, addressValue, onValidationChange, required, isSelectedFromSuggest]);

    const onValueChangeAddressHandler = (value: string) => {
        if (isFromSuggest.current) {
            return;
        }

        validatedChangeRef.current = false;
        setIsFullAddress(false);
        setSuggestedAddressData({});
        setIsSelectedFromSuggest(false);

        if (addressError) {
            setAddressError('');
        }

        setAddressValue((value || '').trim());

        if (!(value || '').trim() && !required) {
            setAddressError('');
            setAllValidatedValue(false);
        }

        onValueChange?.(value);

        if (initial.current) {
            initial.current = false;
        }

        if (blurCheckTimerRef.current) {
            clearTimeout(blurCheckTimerRef.current);
        }

        if ((value || '').trim()) {
            blurCheckTimerRef.current = setTimeout(() => {
                setIsSelectedFromSuggest((current) => {
                    if (!current) {
                        setAddressError(INCOMPLETE_ADDRESS_TEXT);
                        setAllValidatedValue(false);
                    }
                    return current;
                });
            }, 400);
        }
    };

    const onSuggestedValueHandler = (value: CustomInputChangeInterface) => {
        const { index } = value;
        const suggestedAddress = addressDataState[index];

        const isComplete = Boolean(suggestedAddress?.house);

        isFromSuggest.current = true;

        if (suggestTimerRef.current) {
            clearTimeout(suggestTimerRef.current);
        }
        suggestTimerRef.current = setTimeout(() => {
            isFromSuggest.current = false;
        }, 100);

        setAddressError('');
        setIsSelectedFromSuggest(true);

        setIsFullAddress(isComplete);
        setSuggestedAddressData({ ...suggestedAddress });

        onSuggestedValueChange?.(value);
    };

    const setAllValidatedValue = (value: boolean) => {
        validatedChangeRef.current = value;
        validatedSuggestRef.current = value;
    };

    const setAllValidatesCombinedError = (addressCombinedError?: string) => {
        if (addressCombinedError) {
            setAddressError(addressCombinedError);
            setAllValidatedValue(false);
        } else {
            setAddressError('');
            setAllValidatedValue(true);
        }
    };

    const onBlurHandler = () => {
        if (!addressValue) {
            if (!required) {
                setAddressError('');
            }
            return;
        }

        if (!isSelectedFromSuggest) {
            setAddressError(INCOMPLETE_ADDRESS_TEXT);
            return;
        }

        if (!isFullAddress && !addressError) {
            setAddressError(INCOMPLETE_ADDRESS_TEXT);
        }
    };

    const getError = () => {
        if (!addressValue && !required) {
            return '';
        }

        if (validated && addressError === ERROR_FIELD_TEXT) {
            return ERROR_FIELD_TEXT;
        }

        if (validated && addressError === INCOMPLETE_ADDRESS_TEXT) {
            return INCOMPLETE_ADDRESS_TEXT;
        }

        if (validated && addressError === EMPTY_ADDRESS_TEXT) {
            return EMPTY_ADDRESS_TEXT;
        }

        if (!validated) {
            return addressError;
        }

        return '';
    };

    return (
        <Dropdown
            {...props}
            error={getError()}
            options={prepareDataFromDropdownAddress({
                data: addressDataState,
                callback: getAddressComponentItem,
            })}
            validated={addressError ? false : (validatedChangeRef.current && validatedSuggestRef.current) || validated}
            emptyOutputData={getEmptyOutputData(EMPTY_ADDRESS_TEXT)}
            onValueChange={onValueChangeAddressHandler}
            onSuggestedValueChange={onSuggestedValueHandler}
            required={required}
            onBlurHandler={onBlurHandler}
        />
    );
};
